import type { RealtimeTransport } from "../shared/realtimeTelemetry";

const TRANSLATION_CALLS_URL = "https://api.openai.com/v1/realtime/translations/calls";
const REALTIME_CALLS_URL = "https://api.openai.com/v1/realtime/calls";
const CONNECTION_ERROR_MESSAGE = "Unable to connect realtime translation";

export type RealtimeTranslationStatus =
  | "idle"
  | "connecting"
  | "listening"
  | "reconnecting"
  | "muted"
  | "error";

export type RealtimeTranslationSession = {
  sender: RTCRtpSender;
  telemetrySessionId: string;
  transport: RealtimeTransport;
  setMicrophoneEnabled(enabled: boolean): void;
  setAudioEnabled(enabled: boolean): void;
  close(): void;
};

export async function connectRealtimeTranslation(options: {
  microphoneStream: MediaStream;
  audioElement: HTMLAudioElement;
  requestClientSecret: () => Promise<{
    value: string;
    expiresAt: number;
    transport: RealtimeTransport;
    telemetrySessionId: string;
  }>;
  onTranscriptDelta: (delta: string, elapsedMs?: number) => void;
  onStatusChange: (status: RealtimeTranslationStatus) => void;
  onDisconnect?: () => void;
  peerConnectionFactory?: () => RTCPeerConnection;
  fetchImpl?: typeof fetch;
}): Promise<RealtimeTranslationSession> {
  options.onStatusChange("connecting");

  let peerConnection: RTCPeerConnection | undefined;
  let dataChannel: RTCDataChannel | undefined;
  let microphoneTrack: MediaStreamTrack | undefined;
  let closed = false;
  let disconnectSignaled = false;
  let established = false;

  const onTrack = (event: RTCTrackEvent) => {
    const [stream] = event.streams;
    if (stream) {
      options.audioElement.srcObject = stream;
    }
  };

  const onMessage = (event: MessageEvent) => {
    const transcriptDelta = parseTranscriptDelta(event.data);
    if (transcriptDelta) {
      options.onTranscriptDelta(transcriptDelta.delta, transcriptDelta.elapsedMs);
    }
  };

  const onConnectionStateChange = () => {
    if (
      !closed &&
      established &&
      !disconnectSignaled &&
      peerConnection?.connectionState === "failed"
    ) {
      disconnectSignaled = true;
      options.onDisconnect?.();
    }
  };

  const close = () => {
    if (closed) {
      return;
    }

    closed = true;
    dataChannel?.removeEventListener("message", onMessage);
    peerConnection?.removeEventListener("track", onTrack);
    peerConnection?.removeEventListener("connectionstatechange", onConnectionStateChange);
    options.microphoneStream.getTracks().forEach(track => track.stop());
    dataChannel?.close();
    peerConnection?.close();
  };

  try {
    const clientSecret = await options.requestClientSecret();
    if (!clientSecret.value.trim()) {
      throw new Error(CONNECTION_ERROR_MESSAGE);
    }

    const [track] = options.microphoneStream.getAudioTracks();
    if (!track) {
      throw new Error(CONNECTION_ERROR_MESSAGE);
    }
    microphoneTrack = track;

    peerConnection = (options.peerConnectionFactory ?? (() => new RTCPeerConnection()))();
    const sender = peerConnection.addTrack(track, options.microphoneStream);
    dataChannel = peerConnection.createDataChannel("oai-events");
    peerConnection.addEventListener("track", onTrack);
    peerConnection.addEventListener("connectionstatechange", onConnectionStateChange);
    dataChannel.addEventListener("message", onMessage);

    const offer = await peerConnection.createOffer();
    await peerConnection.setLocalDescription(offer);
    const offerSdp = peerConnection.localDescription?.sdp ?? offer.sdp;
    if (!offerSdp) {
      throw new Error(CONNECTION_ERROR_MESSAGE);
    }

    const callsUrl = clientSecret.transport === "realtime"
      ? REALTIME_CALLS_URL
      : TRANSLATION_CALLS_URL;
    const response = await (options.fetchImpl ?? fetch)(callsUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${clientSecret.value}`,
        "Content-Type": "application/sdp",
      },
      body: offerSdp,
    });

    if (!response.ok) {
      throw new Error(CONNECTION_ERROR_MESSAGE);
    }

    const answerSdp = await response.text();
    if (!answerSdp) {
      throw new Error(CONNECTION_ERROR_MESSAGE);
    }
    await peerConnection.setRemoteDescription({ type: "answer", sdp: answerSdp });
    if (peerConnection.connectionState === "failed") {
      throw new Error(CONNECTION_ERROR_MESSAGE);
    }

    established = true;
    options.onStatusChange("listening");
    return {
      sender,
      telemetrySessionId: clientSecret.telemetrySessionId,
      transport: clientSecret.transport,
      setMicrophoneEnabled(enabled) {
        if (closed || !microphoneTrack) {
          return;
        }

        microphoneTrack.enabled = enabled;
        options.onStatusChange(enabled ? "listening" : "muted");
      },
      setAudioEnabled(enabled) {
        if (!closed) {
          options.audioElement.muted = !enabled;
        }
      },
      close,
    };
  } catch {
    close();
    options.onStatusChange("error");
    throw new Error(CONNECTION_ERROR_MESSAGE);
  }
}

function parseTranscriptDelta(data: unknown): { delta: string; elapsedMs?: number } | undefined {
  if (typeof data !== "string") {
    return undefined;
  }

  let event: unknown;
  try {
    event = JSON.parse(data);
  } catch {
    return undefined;
  }

  if (!event || typeof event !== "object") {
    return undefined;
  }

  const message = event as { type?: unknown; delta?: unknown; elapsed_ms?: unknown };
  if (
    message.type !== "session.output_transcript.delta"
    && message.type !== "response.output_audio_transcript.delta"
  ) {
    return undefined;
  }
  if (typeof message.delta !== "string") {
    return undefined;
  }
  return {
    delta: message.delta,
    elapsedMs: typeof message.elapsed_ms === "number" && Number.isFinite(message.elapsed_ms)
      ? message.elapsed_ms
      : undefined,
  };
}
